import { type ReactNode, type Ref, useState } from 'react'
import { CheckCircleIcon, PencilSimpleIcon } from '@phosphor-icons/react'
import * as Mtds from '@mattilsynet/design/react'
import { MarkdownContent } from '../markdown-content/MarkdownContent'
import { MarkdownEditor } from '../markdown-editor/MarkdownEditor'

type MarkdownCardProps = {
  title: string
  value?: string
  emptyText: string
  maxLength: number
  editAriaLabel?: string
  placeholder?: string
  onSave?: (value: string) => Promise<void>
  isSaving?: boolean
  updated?: boolean
  onDirtyChange?: (isDirty: boolean) => void
  viewClassName?: string
  viewRef?: Ref<HTMLDivElement>
  children?: ReactNode
}

export const MarkdownCard = ({
  title,
  value = '',
  emptyText,
  maxLength,
  editAriaLabel = `Rediger ${title.toLowerCase()}`,
  placeholder,
  onSave,
  isSaving = false,
  updated,
  onDirtyChange,
  viewClassName,
  viewRef,
  children
}: MarkdownCardProps) => {
  const [isEditing, setIsEditing] = useState(false)
  const [draft, setDraft] = useState(value)
  const [isOverLimit, setIsOverLimit] = useState(false)
  const isUnsaved = draft !== value
  const isEmpty = value.trim() === ''

  const startEditing = () => {
    setDraft(value)
    setIsEditing(true)
    onDirtyChange?.(false)
  }

  const updateDraft = (next: string) => {
    setDraft(next)
    onDirtyChange?.(next !== value)
  }

  const finishEditing = async () => {
    if (isOverLimit) {
      Mtds.toast.danger(`${title} er for lang (maks ${maxLength} tegn)`)
      return
    }

    if (isUnsaved && onSave) {
      await onSave(draft)
    }
    setIsEditing(false)
    onDirtyChange?.(false)
  }

  if (isEditing) {
    return (
      <Mtds.Card as={Mtds.Grid} data-gap='2'>
        <Mtds.Flex data-justify='space-between' data-align='center'>
          <Mtds.Heading as='h3' data-size='sm'>
            {title}
          </Mtds.Heading>
          <Mtds.Button
            data-variant='primary'
            type='button'
            onClick={() => void finishEditing()}
            disabled={isSaving}
          >
            {isSaving ? <Mtds.Spinner data-size='sm' /> : 'Ferdig'}
          </Mtds.Button>
        </Mtds.Flex>
        <MarkdownEditor
          value={draft}
          onChange={updateDraft}
          charLimit={maxLength}
          onValidChange={(valid) => setIsOverLimit(!valid)}
          placeholder={placeholder}
          disabled={isSaving}
        />
      </Mtds.Card>
    )
  }

  return (
    <Mtds.Card as={Mtds.Grid} data-gap='2'>
      <Mtds.Flex data-justify='space-between' data-align='center'>
        <Mtds.Heading as='h3' data-size='sm'>
          {title}
        </Mtds.Heading>
        {onSave && (
          <Mtds.Button
            data-variant='tertiary'
            type='button'
            aria-label={editAriaLabel}
            onClick={startEditing}
          >
            <PencilSimpleIcon aria-hidden />
          </Mtds.Button>
        )}
      </Mtds.Flex>
      <Mtds.Flex data-justify='space-between' data-align='center' data-nowrap>
        <div ref={viewRef} className={viewClassName}>
          {isEmpty ? (
            <Mtds.Muted>{emptyText}</Mtds.Muted>
          ) : (
            <MarkdownContent content={value} />
          )}
        </div>
        {!isEmpty && updated && <CheckCircleIcon />}
      </Mtds.Flex>
      {children}
    </Mtds.Card>
  )
}
